import axios from "axios";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import DatePicker from "react-datepicker";

import CircularProgress from "@material-ui/core/CircularProgress";
import AutocompleteInput from "./AutocompleteInput";
import PopupModal from "./Modal";
import { LessonPlanAPIs } from "../api";

const periods = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

export default function EditLessonPlanModal({
  userId,
  assignedClasses,
  grades,
  planId,
  formData,
  syncLessonPlan,
}) {
  const defaultValues = useMemo(() => {
    return {
      time: formData?.time || "",
      classesTeaching: formData?.classesTeaching || "",
      level: formData?.level || "",
      gradeName: formData?.gradeName || "",
      moduleName: formData?.moduleName || "",
      subjectId: undefined,
      chapterName: undefined,
      topicName: undefined,
    };
  }, [formData]);

  const { register, reset, handleSubmit, watch, setValue, getValues } = useForm(
    {
      defaultValues,
    }
  );
  const closeButtonRef = useRef(null);
  const [popup, setPopup] = useState({});
  const [grade, setGrade] = useState({ grade: "", sections: [] });
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState("");
  const loadTimeoutRef = useRef(null);
  const [resourceData, setResourceData] = useState({
    fromDate: formData.fromDate,
  });
  const [addContentMessage, setAddContentMessage] = useState("");
  const [addContentLoader, setAddContentLoader] = useState(false);
  // resource data handle
  const handleResourceFromDateChange = (date) => {
    setResourceData({ ...resourceData, fromDate: date });
  };

  const chapterNameValue = watch("chapterName");
  const topicNameValue = watch("topicName");
  const moduleNameValue = watch("moduleName");

  function closeModal() {
    if (!closeButtonRef.current) return;
    closeButtonRef.current.click();
  }

  function getSections(event) {
    reset({
      sectionName: "",
      subjectId: "",
      chapterName: "",
      chapterId: "",
      topicName: "",
      topicId: "",
    });
    setGrade({ name: "", sections: [] });
    setSubjects([]);
    setChapters([]);
    setTopics([]);
    const selectedGrade = event.target.value;
    if (!selectedGrade) return;
    const newSections = grades[selectedGrade];
    setGrade({ grade: selectedGrade, sections: newSections });
  }

  function getSubjects(section) {
    reset({
      subjectId: "",
      chapterName: "",
      chapterId: "",
      topicName: "",
      topicId: "",
    });
    setSubjects([]);
    setChapters([]);
    setTopics([]);

    const tempArr = [];
    const selectedSection = `${grade.grade}-${section}`;

    assignedClasses.length &&
      assignedClasses.forEach((assignedClass) => {
        if (assignedClass._id === selectedSection) {
          assignedClass.subjects.forEach((data) => {
            tempArr.push({
              subject: data.subject,
              subjectId: data.subjectId,
            });
          });
        }
      });

    setSubjects([...tempArr]);
  }

  const getChapters = async (e, initialLoad) => {
    reset({
      chapterName: "",
      chapterId: "",
      topicName: "",
      topicId: "",
    });
    setChapters([]);
    setTopics([]);

    const authToken = localStorage.getItem("authToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ORIGIN}/api/v1/subjects/${e.target.value}`,
        {
          headers: {
            token: authToken,
          },
        }
      );

      if (response.data.status === "success") {
        let dt = response.data.data.subject.chapters.filter(
          (item) => !item.disabled
        );
        setChapters(dt);
        if (initialLoad) {
          setTimeout(() => {
            setValue("chapterName", formData?.chapterName);
            fetchTopics(formData?.chapterName, true);
          }, 300);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTopics = useCallback(
    (chapterName) => {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = setTimeout(async () => {
        const chapter = chapters.find((chpt) => chpt.name === chapterName);

        if (!chapter) {
          setValue("topicName", formData?.topicName);
          return;
        }

        setLoading("topic-loading");
        const authToken = localStorage.getItem("authToken");
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_ORIGIN}/api/v1/chapters/${chapter._id}`,
            {
              headers: {
                token: authToken,
              },
            }
          );
          setValue("chapterId", chapter._id);
          if (response.data.status === "success") {
            let dt = response.data.data.chapter.topics.filter(
              (item) => !item.disabled
            );
            setTopics(dt);
          }
        } catch (error) {
          console.log(error);
        }
        setLoading("");
        clearTimeout(loadTimeoutRef.current);
      }, 1000);
    },
    [topics, setTopics, chapters, formData]
  );

  function selectSections(selectedOption) {
    const section = selectedOption.target.value;
    if (section !== "Select Section") getSubjects(section);
  }

  //Select default values
  useEffect(() => {
    getSections({ target: { value: formData.gradeName } }, true);
  }, [formData]);

  useEffect(() => {
    if (!grade?.grade || getValues("sectionName")) return;
    setValue("sectionName", formData?.sectionName);
    getSubjects(formData.sectionName);
  }, [formData, grade]);

  useEffect(() => {
    if (!subjects.length || !formData?.subjectId || getValues("subjectId"))
      return;
    const subject = subjects.find(
      (sub) => sub.subjectId === formData.subjectId
    );
    if (subject) {
      setValue("subjectId", formData?.subjectId);
      getChapters({ target: { value: formData?.subjectId } }, true);
    }
  }, [formData, subjects]);

  useEffect(() => {
    if (!chapters.length || !formData?.chapterName || getValues("chapterName"))
      return;
    setValue("chapterName", formData?.chapterName);
    fetchTopics(formData?.chapterName, true);
  }, [formData, chapters]);

  useEffect(() => {
    if (!topics.length || !formData?.topicName || getValues("topicName"))
      return;
    setValue("topicName", formData?.topicName);
  }, [formData, topics]);

  useEffect(async () => {
    if (!chapterNameValue) return;
    setValue("topicName", "");
    setValue("topicId", "");
    setValue("chapterId", "");
    setTopics([]);
    fetchTopics(chapterNameValue);
  }, [chapterNameValue]);

  const submitResourceForm = async (data) => {
    setAddContentMessage("");
    setAddContentLoader(true);

    try {
      const section = assignedClasses.find(
        (sec) => sec._id === `${data.gradeName}-${data.sectionName}`
      );
      if (!section) {
        throw new Error("Please select grade and section");
      }

      const topic = topics.find((topic) => topic.name === data.topicName);

      //const formdata = new FormData();
      const sectionSubject = section.subjects[0];
      const payload = {
        sectionId: sectionSubject.sectionId,
        subjectId: data.subjectId,
        chapterId: data.chapterId || undefined,
        customChapter: !data.chapterId ? data.chapterName : undefined,
        topicId: topic?._id || undefined,
        customTopic: !topic?._id ? data.topicName : undefined,
        customModule: data?.moduleName || undefined,
        startDate: new Date(
          resourceData.fromDate || formData?.fromDate
        ).getTime(),
        time: data.time,
        level: data.level,
        classesTeaching: data.classesTeaching,
      };

      const res = await axios.patch(
        LessonPlanAPIs.editLessonPlan(userId, planId),
        payload,
        {
          headers: {
            token: localStorage.getItem("authToken"),
          },
          validateStatus: () => true,
        }
      );
      if (res.data.status !== "success") throw res.data;

      const [gradeName, sectionName] = (section._id || "").split("-");

      const t = setTimeout(() => {
        syncLessonPlan(planId, {
          ...payload,
          sectionId: {
            _id: sectionSubject.sectionId,
            grade: gradeName,
            section: sectionName,
          },
          subjectId: {
            _id: data.subjectId,
            name:
              section.subjects.find((sub) => sub.subjectId === data.subjectId)
                ?.subjectData?.name || "-",
          },
          chapterId: payload.chapterId
            ? {
                _id: payload.chapterId,
                name: chapterNameValue,
              }
            : undefined,
          topicId: topic,
          startDate: new Date(
            resourceData.fromDate || formData?.fromDate
          ).toISOString(),
        });

        clearTimeout(t);
      }, 1000);

      closeModal();
      setPopup({
        open: true,
        status: "Success",
        message: "Lesson plan updated",
      });
    } catch (error) {
      console.log(error);
      if (error.response?.status === "fail") {
        setAddContentMessage(error.response.message);
      } else {
        setAddContentMessage(error.message || "Try Again.");
      }
    }

    setAddContentLoader(false);
  };

  const clearOnClose = () => {
    setAddContentLoader(false);
    setAddContentMessage("");
  };

  return (
    <Fragment>
      <div
        className="modal fade resourceModel mt-5"
        id={`edit_${planId}`}
        tabIndex="-1"
        aria-labelledby="resourceModalLabel"
        data-bs-backdrop="static"
        aria-hidden="true"
      >
        <div className="modal-dialog lp-modal-dailog">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id="resourceModalLabel">
                EDIT LESSON PLAN
              </h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleSubmit(submitResourceForm)}>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-sm">
                    <div className="col-sm">
                      <label htmlFor="s" className="form-label">
                        Period
                      </label>
                      <select
                        className="form-select"
                        id="time"
                        {...register("time")}
                      >
                        <option key={0} value={""}>
                          Select Period
                        </option>
                        {periods.map((period, index) => {
                          return (
                            <option key={index} value={period}>
                              {period}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                  <div className="col-sm">
                    <label htmlFor="level" className="mb-2">
                      Level
                    </label>
                    <input
                      type="text"
                      id={"level"}
                      name={"level"}
                      className="form-control form-control-sm"
                      placeholder={"Enter level"}
                      {...register("level")}
                      style={{ fontSize: "1.05rem" }}
                      autoComplete="off"
                    />
                  </div>
                  <div className="col-sm">
                    <div className="col-sm">
                      <label htmlFor="classes-teacher" className="form-label">
                        Classes Teaching
                      </label>
                      <input
                        type="text"
                        id={"classes-teaching"}
                        name={"classesTeaching"}
                        className="form-control form-control-sm"
                        {...register("classesTeaching")}
                        style={{ fontSize: "1.05rem" }}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm">
                    <label htmlFor="classSelect" className="form-label">
                      Class
                    </label>
                    <select
                      className="form-select"
                      id="classSelect"
                      {...register("gradeName")}
                      onChange={getSections}
                    >
                      <option defaultValue="">Select Class</option>
                      {Object.keys(grades).length &&
                        Object.keys(grades).map((grade, index) => {
                          return (
                            <option key={index} value={grade}>
                              {grade}
                            </option>
                          );
                        })}
                    </select>
                  </div>

                  <div className="col-sm">
                    <label htmlFor="sectionSelect" className="form-label">
                      Section
                    </label>
                    <select
                      className="form-select"
                      id="sectionSelect"
                      {...register("sectionName")}
                      onChange={selectSections}
                    >
                      <option defaultValue="">Select Section</option>
                      {grade.sections.length &&
                        grade.sections.map((grade, index) => {
                          return (
                            <option key={index} value={grade.section}>
                              {grade.section}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                  <div className="col-sm">
                    <label htmlFor="subjectSelect" className="form-label">
                      Subject
                    </label>
                    <select
                      className="form-select"
                      id="subjectSelect"
                      {...register("subjectId")}
                      onChange={getChapters}
                    >
                      <option defaultValue="" value={""}>
                        Select Subject
                      </option>
                      {subjects.map((subject, index) => (
                        <option key={index} value={subject.subjectId}>
                          {subject.subject}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm">
                    <AutocompleteInput
                      name={"chapterSelect"}
                      label={"Chapter"}
                      placeholder={"Select Chapter"}
                      updateValue={(value) => setValue("chapterName", value)}
                      suggestions={chapters.map((chpt) => ({
                        name: chpt.name,
                        value: chpt._id,
                      }))}
                      value={chapterNameValue}
                      register={register("chapterName")}
                      disabled={loading === "topic-loading"}
                    />
                  </div>
                  <div className="col-sm">
                    <AutocompleteInput
                      name={"topicSelect"}
                      label={"Topic"}
                      placeholder={"Select Topic"}
                      updateValue={(value) => setValue("topicName", value)}
                      suggestions={topics.map((topic) => ({
                        name: topic.name,
                        value: topic._id,
                      }))}
                      value={topicNameValue}
                      register={register("topicName")}
                      disabled={[
                        "subject-loading",
                        "topic-loading",
                        "chapter-loading",
                      ].includes(loading)}
                    />
                  </div>
                  <div className="col-sm">
                    <AutocompleteInput
                      name={"moduleSelect"}
                      label={"Sub Topic"}
                      placeholder={"Enter Sub Topic"}
                      updateValue={(value) => setValue("moduleName", value)}
                      suggestions={[]}
                      value={moduleNameValue}
                      register={register("moduleName")}
                    />
                  </div>
                  <div className="col-sm">
                    <label htmlFor="resourceFromDate" className="mb-2">
                      Date
                    </label>
                    <DatePicker
                      selected={resourceData.fromDate}
                      dateFormat="dd/MM/yyyy"
                      id="resourceFromDate"
                      className="form-control"
                      onChange={handleResourceFromDateChange}
                      placeholderText="From Date"
                    />
                  </div>
                </div>
              </div>

              {addContentMessage && (
                <div className="text-center">
                  <p style={{ color: "#FF075B", fontWeight: "600" }}>
                    {addContentMessage}
                  </p>
                </div>
              )}

              <div className="mb-3 modal_button_container">
                <div className="row">
                  <div className="col d-grid">
                    <button
                      type="button"
                      className="btn resourceModelBtnCancel"
                      data-bs-dismiss="modal"
                      ref={closeButtonRef}
                      onClick={clearOnClose}
                    >
                      Close
                    </button>
                  </div>
                  <div className="col d-grid">
                    {addContentLoader ? (
                      <div className="text-center">
                        <CircularProgress
                          style={{ color: "#FF075B", fontWeight: "600" }}
                        />
                      </div>
                    ) : (
                      <button
                        type="submit"
                        className="btn resourceModelBtnApply"
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <PopupModal
        open={popup.open}
        heading={popup.status}
        message={popup.message}
      >
        <button
          style={{
            color: "#414141",
          }}
          onClick={() => setPopup({})}
        >
          Close
        </button>
      </PopupModal>
    </Fragment>
  );
}
